import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { ModuleId } from '@prody/shared';
import { businessPath, modulePath, path } from '~/app/navigation';
import { Menu, Tooltip } from '~/design-system';
import { useAppearance, type Dock } from '~/platform/appearance/appearance';
import { useNavPrefs, NO_FAVORITES } from '~/platform/nav/navPrefs';
import type { ResolvedRegistry } from '~/platform/registry/registry';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import './NavRail.css';

/* ------------------------------------------------------------------ *
 * The rail is a loading dock — and, like a Windows taskbar, it docks
 * to any edge. Left and right keep the vertical rail. Top and bottom
 * become a bar: pinned favourites are always one click away, groups
 * open as flyouts on hover (70ms intent delay, forgiving 120ms
 * close) or on click for touch.
 *
 * Favourites are the answer to "долго листать": a star appears on
 * hover of any item; pinned modules gather in their own group at the
 * top of the rail, in pin order, per user and per business.
 *
 * Below 720px the stored dock is ignored and the rail renders its
 * vertical markup, which the mobile stylesheet reshapes into the
 * bottom icon bar — a phone already IS bottom-docked.
 * ------------------------------------------------------------------ */

type Entry = ResolvedRegistry['nav'][number]['entries'][number];

interface NavContext {
  label: (entry: Entry) => string;
  toFor: (entry: Entry) => string;
  isActive: (entry: Entry) => boolean;
  favIds: readonly ModuleId[];
  togglePin: (id: ModuleId) => void;
  favEntries: Entry[];
}

export function NavRail({ registry, dock }: { registry: ResolvedRegistry; dock: Dock }) {
  const { t } = useTranslation();
  const { business } = useBusiness();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const base = businessPath(business.slug);

  const favIds = useNavPrefs((s) => s.favorites[business.slug] ?? NO_FAVORITES);
  const toggleFavorite = useNavPrefs((s) => s.toggleFavorite);

  const ctx = useMemo<NavContext>(() => {
    const label = (entry: Entry) =>
      t(`${entry.moduleId}:nav`, { defaultValue: t(entry.labelKey, { defaultValue: entry.to }) });
    const toFor = (entry: Entry) => modulePath(business.slug, entry.to);
    const isActive = (entry: Entry) =>
      entry.to
        ? pathname === toFor(entry) || pathname.startsWith(`${toFor(entry)}/`)
        : pathname === base || pathname === `${base}/`;
    const all = registry.nav.flatMap((g) => g.entries);
    const favEntries = favIds
      .map((id) => all.find((e) => e.moduleId === id))
      .filter((e): e is Entry => Boolean(e));
    return {
      label,
      toFor,
      isActive,
      favIds,
      togglePin: (id) => toggleFavorite(business.slug, id),
      favEntries,
    };
  }, [t, business.slug, pathname, base, registry, favIds, toggleFavorite]);

  return dock === 'top' || dock === 'bottom' ? (
    <BarNav registry={registry} ctx={ctx} edge={dock} pathname={pathname} />
  ) : (
    <SideNav registry={registry} ctx={ctx} edge={dock} businessName={business.name} base={base} />
  );
}

/* ================================================================== *
 * Vertical rail — left or right edge.
 * ================================================================== */

function SideNav({
  registry,
  ctx,
  edge,
  businessName,
  base,
}: {
  registry: ResolvedRegistry;
  ctx: NavContext;
  edge: 'left' | 'right';
  businessName: string;
  base: string;
}) {
  const { t } = useTranslation();
  const collapsed = useAppearance((s) => s.navCollapsed);
  const toggle = useAppearance((s) => s.toggleNav);
  const tooltipSide = edge === 'right' ? 'left' : 'right';

  const renderEntry = (entry: Entry, inFavorites: boolean) => {
    const active = ctx.isActive(entry);
    const Icon = entry.icon;
    const pinned = ctx.favIds.includes(entry.moduleId);
    const name = ctx.label(entry);

    const link = (
      <Link
        to={path(ctx.toFor(entry))}
        className="pd-rail__item"
        data-active={active || undefined}
        aria-current={active ? 'page' : undefined}
      >
        <span className="pd-rail__icon" aria-hidden="true">
          <Icon />
        </span>
        {!collapsed ? <span className="pd-rail__label">{name}</span> : null}
      </Link>
    );

    return (
      <li key={`${inFavorites ? 'fav-' : ''}${entry.moduleId}`} className="pd-rail__li">
        {collapsed ? (
          <Tooltip content={name} side={tooltipSide}>
            {link}
          </Tooltip>
        ) : (
          link
        )}
        {!collapsed ? (
          <PinButton pinned={pinned} onToggle={() => ctx.togglePin(entry.moduleId)} />
        ) : null}
      </li>
    );
  };

  return (
    <nav className="pd-rail" data-collapsed={collapsed || undefined} aria-label="Main">
      <div className="pd-rail__brand">
        <Link to={path(base)} className="pd-rail__mark" aria-label={businessName}>
          <svg viewBox="0 0 28 24" aria-hidden="true">
            <rect x="1" y="5" width="26" height="18" rx="1" fill="none" stroke="currentColor" strokeWidth="1.25" />
            <path d="M1 5 3.5 1h21L27 5" fill="none" stroke="currentColor" strokeWidth="1.25" />
          </svg>
          <span className="pd-rail__initials">{initials(businessName)}</span>
        </Link>
        {!collapsed ? (
          <span className="pd-rail__name truncate" title={businessName}>
            {businessName}
          </span>
        ) : null}
      </div>

      <div className="pd-rail__groups scroll-y">
        {/* Pinned favourites — the shortcut shelf. */}
        {ctx.favEntries.length > 0 ? (
          <div className="pd-rail__group" data-favorites>
            {!collapsed ? (
              <p className="stencil pd-rail__group-title">
                <StarGlyph filled className="pd-rail__group-star" />
                {t('nav.favorites')}
              </p>
            ) : (
              <span className="pd-rail__group-rule" aria-hidden="true" />
            )}
            <ul>{ctx.favEntries.map((entry) => renderEntry(entry, true))}</ul>
          </div>
        ) : null}

        {registry.nav.map(({ group, entries }) => (
          <div key={group} className="pd-rail__group">
            {!collapsed ? (
              <p className="stencil pd-rail__group-title">{t(`nav.${group}`)}</p>
            ) : (
              <span className="pd-rail__group-rule" aria-hidden="true" />
            )}
            <ul>{entries.map((entry) => renderEntry(entry, false))}</ul>
          </div>
        ))}
      </div>

      <div className="pd-rail__foot">
        <button
          type="button"
          className="pd-rail__collapse"
          onClick={toggle}
          aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25" aria-hidden="true">
            <path d={collapsed ? 'M6 3l5 5-5 5' : 'M10 3 5 8l5 5'} />
          </svg>
        </button>
        <DockMenu />
      </div>
    </nav>
  );
}

/* ================================================================== *
 * Horizontal bar — top or bottom edge, taskbar-style.
 * Favourites sit inline for one-click access; groups open on hover.
 * ================================================================== */

function BarNav({
  registry,
  ctx,
  edge,
  pathname,
}: {
  registry: ResolvedRegistry;
  ctx: NavContext;
  edge: 'top' | 'bottom';
  pathname: string;
}) {
  const { t } = useTranslation();
  // Click-open state for touch and keyboard; mouse users get CSS :hover.
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  useEffect(() => setOpenGroup(null), [pathname]);

  return (
    <nav className="pd-rail pd-rail--bar" data-edge={edge} aria-label="Main">
      {ctx.favEntries.map((entry) => {
        const Icon = entry.icon;
        return (
          <Link
            key={entry.moduleId}
            to={path(ctx.toFor(entry))}
            className="pd-bar__fav"
            data-active={ctx.isActive(entry) || undefined}
            aria-current={ctx.isActive(entry) ? 'page' : undefined}
            title={ctx.label(entry)}
          >
            <span className="pd-rail__icon" aria-hidden="true">
              <Icon />
            </span>
            <span className="pd-bar__fav-label">{ctx.label(entry)}</span>
          </Link>
        );
      })}
      {ctx.favEntries.length > 0 ? <span className="pd-bar__sep" aria-hidden="true" /> : null}

      {registry.nav.map(({ group, entries }) => {
        const groupActive = entries.some(ctx.isActive);
        const open = openGroup === group;
        return (
          <div
            key={group}
            className="pd-bar__group"
            data-open={open || undefined}
            onPointerLeave={() => open && setOpenGroup(null)}
          >
            <button
              type="button"
              className="pd-bar__trigger"
              data-active={groupActive || undefined}
              aria-haspopup="true"
              aria-expanded={open}
              onClick={() => setOpenGroup(open ? null : group)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setOpenGroup(null);
              }}
            >
              {t(`nav.${group}`)}
              <span className="pd-bar__chev" aria-hidden="true">
                {edge === 'bottom' ? '▴' : '▾'}
              </span>
            </button>

            <div className="pd-bar__flyout" role="group" aria-label={t(`nav.${group}`)}>
              <ul>
                {entries.map((entry) => {
                  const Icon = entry.icon;
                  const active = ctx.isActive(entry);
                  return (
                    <li key={entry.moduleId} className="pd-rail__li">
                      <Link
                        to={path(ctx.toFor(entry))}
                        className="pd-bar__link"
                        data-active={active || undefined}
                        aria-current={active ? 'page' : undefined}
                      >
                        <span className="pd-rail__icon" aria-hidden="true">
                          <Icon />
                        </span>
                        <span className="truncate">{ctx.label(entry)}</span>
                      </Link>
                      <PinButton
                        pinned={ctx.favIds.includes(entry.moduleId)}
                        onToggle={() => ctx.togglePin(entry.moduleId)}
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        );
      })}

      <span className="grow" aria-hidden="true" />
      <DockMenu />
    </nav>
  );
}

/* ================================================================== *
 * Shared bits
 * ================================================================== */

function PinButton({ pinned, onToggle }: { pinned: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  const name = pinned ? t('nav.unpin') : t('nav.pin');
  return (
    <button
      type="button"
      className="pd-rail__pin"
      data-pinned={pinned || undefined}
      aria-label={name}
      title={name}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
    >
      <StarGlyph filled={pinned} />
    </button>
  );
}

function DockMenu() {
  const { t } = useTranslation();
  const dock = useAppearance((s) => s.dock);
  const setDock = useAppearance((s) => s.setDock);

  const OPTIONS: { value: Dock; labelKey: string }[] = [
    { value: 'left', labelKey: 'nav.dockLeft' },
    { value: 'right', labelKey: 'nav.dockRight' },
    { value: 'top', labelKey: 'nav.dockTop' },
    { value: 'bottom', labelKey: 'nav.dockBottom' },
  ];

  return (
    <Menu
      label={t('nav.dock')}
      align="end"
      trigger={
        <button type="button" className="pd-rail__dockbtn" aria-label={t('nav.dock')} title={t('nav.dock')}>
          <DockGlyph side={dock} />
        </button>
      }
      items={OPTIONS.map((o) => ({
        key: o.value,
        label: t(o.labelKey),
        icon: <DockGlyph side={o.value} strong={o.value === dock} />,
        onSelect: () => setDock(o.value),
      }))}
    />
  );
}

function StarGlyph({ filled, className }: { filled?: boolean; className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width="1em"
      height="1em"
      className={className}
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinejoin="miter"
      aria-hidden="true"
    >
      <path d="M8 2.2l1.75 3.55 3.9.55-2.85 2.75.7 3.85L8 11.05 4.5 12.9l.7-3.85L2.35 6.3l3.9-.55z" />
    </svg>
  );
}

/** A tiny screen with the docked bar drawn on the chosen side. */
function DockGlyph({ side, strong }: { side: Dock; strong?: boolean }) {
  const bar: Record<Dock, ReactNode> = {
    left: <rect x="2" y="3" width="3.5" height="10" fill="currentColor" stroke="none" />,
    right: <rect x="10.5" y="3" width="3.5" height="10" fill="currentColor" stroke="none" />,
    top: <rect x="2" y="3" width="12" height="3.5" fill="currentColor" stroke="none" />,
    bottom: <rect x="2" y="9.5" width="12" height="3.5" fill="currentColor" stroke="none" />,
  };
  return (
    <svg
      viewBox="0 0 16 16"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth={strong ? 1.6 : 1.1}
      aria-hidden="true"
    >
      <rect x="1.5" y="2.5" width="13" height="11" rx="1" />
      {bar[side]}
    </svg>
  );
}

const initials = (name: string): string =>
  name
    .split(/[\s-]+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
